import { Link } from '@tanstack/react-router';
import { Home, MoreHorizontal, Users, Wheat } from 'lucide-react';
import { useState } from 'react';
import { authClient } from '@/lib/auth';
import type { Role } from '@/lib/schemas';
import { cn } from '@/lib/utils';

interface BottomNavProps {
  role: Role;
}

export function BottomNav({ role }: BottomNavProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const canLog = role === 'farmer' || role === 'helper';

  return (
    <>
      {/* More menu overlay */}
      {moreOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default"
          onClick={() => setMoreOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setMoreOpen(false)}
          aria-label="Close menu"
          tabIndex={-1}
        />
      )}

      {/* More menu popup */}
      {moreOpen && (
        <div className="fixed bottom-20 right-4 z-50 rounded-lg bg-ndd-800 shadow-xl">
          <Link
            to="/ration"
            className="block px-6 py-4 text-white hover:bg-ndd-700 rounded-t-lg"
            onClick={() => setMoreOpen(false)}
          >
            Current Ration
          </Link>
          <Link
            to="/alerts"
            className="block px-6 py-4 text-white hover:bg-ndd-700"
            onClick={() => setMoreOpen(false)}
          >
            Alerts
          </Link>
          <button
            type="button"
            className="w-full px-6 py-4 text-left text-red-300 hover:bg-ndd-700 rounded-b-lg"
            onClick={async () => {
              setMoreOpen(false);
              await authClient.signOut();
              window.location.href = '/login';
            }}
          >
            Sign Out
          </button>
        </div>
      )}

      {/* Bottom nav bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 flex h-16 items-center bg-ndd-950 border-t border-ndd-800 pb-[env(safe-area-inset-bottom)]"
        aria-label="Main navigation"
      >
        <Link
          to="/"
          className={cn(
            'flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs text-ndd-400 min-h-[48px]'
          )}
          activeProps={{ className: 'text-white' }}
          activeOptions={{ exact: true }}
        >
          <Home size={22} />
          <span>Home</span>
        </Link>

        <Link
          to="/herd"
          className="flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs text-ndd-400 min-h-[48px]"
          activeProps={{ className: 'text-white' }}
        >
          <Users size={22} />
          <span>Herd</span>
        </Link>

        {canLog ? (
          <Link
            to="/log/feeding"
            className="flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs text-ndd-400 min-h-[48px]"
            activeProps={{ className: 'text-white' }}
          >
            <Wheat size={22} />
            <span>Feed</span>
          </Link>
        ) : (
          <Link
            to="/ration"
            className="flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs text-ndd-400 min-h-[48px]"
            activeProps={{ className: 'text-white' }}
          >
            <Wheat size={22} />
            <span>Ration</span>
          </Link>
        )}

        <button
          type="button"
          className="flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs text-ndd-400 min-h-[48px]"
          onClick={() => setMoreOpen((v) => !v)}
          aria-expanded={moreOpen}
          aria-haspopup="menu"
        >
          <MoreHorizontal size={22} />
          <span>More</span>
        </button>
      </nav>
    </>
  );
}
